import {
  buildProductMaterialCharacteristics,
  type DeepPartial,
  getCompleteItemCharacteristics,
  hasDistinctItemCharacteristics,
  ItemScopeEnum,
  ItemTypeEnum,
  type MaterialCharacteristics,
  materialCharacteristicsSchema,
  type ProductMaterialCharacteristics,
  productMaterialCharacteristicsSchema,
  SOLIDARIZED_PRODUCT_TYPE,
} from "@jasper/client-models";
import {
  ProductMaterialCharacteristicsForm,
  validateSchema,
  type ValidationErrors,
} from "@jasper/forms";
import {
  useItemMaterialsByItemType,
  type UpdateProductArgs,
} from "@jasper/use-cases";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ConfigurableProduct, ProductStepProps } from "./types";
import { getGroupTeeth } from "../productGroupTeeth";
import { useRegisterStepSave } from "./useRegisterStepSave";

export type InlayCoreCrownStepProps = ProductStepProps;

type PersistedItem = ConfigurableProduct["items"][number];

type FormValue = {
  product: DeepPartial<
    ProductMaterialCharacteristics & { createCrown: boolean }
  >;
};

const isToothScopedPersistedItem = (
  item: PersistedItem
): item is Extract<PersistedItem, { tooth: number }> =>
  item.scope === ItemScopeEnum.tooth;

const isCrownPersistedItem = (
  item: PersistedItem
): item is Extract<
  PersistedItem,
  { tooth: number; type: typeof ItemTypeEnum.crown }
> => isToothScopedPersistedItem(item) && item.type === ItemTypeEnum.crown;

const validateSelection = ({ value }: { value: FormValue["product"] }) => {
  if (!value?.createCrown) {
    return undefined;
  }

  const result = validateSchema(productMaterialCharacteristicsSchema, value);
  return result.success ? undefined : result.errors;
};

const formatProductSelection = ({
  product,
  value,
}: {
  product: ConfigurableProduct;
  value: FormValue;
}): UpdateProductArgs => {
  const valueItems = getCompleteItemCharacteristics(value.product?.items);

  return {
    productId: product.id,
    items: product.teeth.flatMap(tooth => {
      const persistedMatchingItems = product.items
        .filter(isToothScopedPersistedItem)
        .filter(item => item.tooth === tooth);

      const persistedInlayCoreItem = persistedMatchingItems.find(
        item => item.type === ItemTypeEnum.inlayCore
      );

      // The inlay-core is created by the tooth step: keep the tooth as is rather than dropping it.
      if (!persistedInlayCoreItem) {
        return persistedMatchingItems;
      }

      const persistedCrownItem = persistedMatchingItems.find(
        item => item.type === ItemTypeEnum.crown
      );

      const valueMatchingItem = valueItems.find(item => item.tooth === tooth);

      if (!value.product?.createCrown) {
        return [persistedInlayCoreItem];
      }

      return [
        persistedInlayCoreItem,
        {
          ...persistedCrownItem,
          scope: ItemScopeEnum.tooth,
          tooth,
          type: ItemTypeEnum.crown,
          materialCharacteristics:
            valueMatchingItem?.materialCharacteristics ??
            persistedCrownItem?.materialCharacteristics,
        },
      ];
    }),
  };
};

export const InlayCoreCrownStep = ({
  stepKey,
  products,
  orderConfiguration,
  registerStepSave,
}: InlayCoreCrownStepProps) => {
  const {
    isLoading: isItemMaterialChoicesLoading,
    itemMaterials,
    itemShadeTypes,
    itemShades,
  } = useItemMaterialsByItemType({ itemType: ItemTypeEnum.crown });
  const { isLoading: isOrderLoading, updateProducts } = orderConfiguration;
  const { t } = useTranslation("product-workflows", {
    keyPrefix: "steps.inlayCoreCrown",
  });

  const [product] = products;
  const groupTeeth = useMemo(() => getGroupTeeth(products), [products]);

  const persistedItems = useMemo(
    () =>
      products
        .flatMap(configuredProduct => configuredProduct.items)
        .filter(isCrownPersistedItem),
    [products]
  );

  const defaultIsPerTooth = useMemo(
    () => hasDistinctItemCharacteristics(persistedItems),
    [persistedItems]
  );

  // The crown items are the persisted state of the checkbox: the tooth step creates them.
  const defaultValues = useMemo(
    () => ({
      product: {
        createCrown: persistedItems.length > 0,
        ...buildProductMaterialCharacteristics(groupTeeth, persistedItems),
      },
    }),
    [groupTeeth, persistedItems]
  );

  const saveSelection = async (value: FormValue) => {
    try {
      await updateProducts(
        products.map(configuredProduct =>
          formatProductSelection({ product: configuredProduct, value })
        )
      );
    } catch {
      notifications.show({
        title: t("errorNotificationTitle", { keyPrefix: "common" }),
        message: t("errorNotificationMessage", { keyPrefix: "common" }),
        color: "red",
      });
    }
  };

  const form = useForm({
    defaultValues,
    listeners: {
      onChangeDebounceMs: 250,
      onBlurDebounceMs: 250,
      onChange: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
      onBlur: ({ formApi }) => {
        if (
          !formApi.state.isValid ||
          formApi.state.isSubmitting ||
          formApi.state.isPristine
        )
          return;
        formApi.handleSubmit();
      },
    },
    onSubmit: ({ value }) => saveSelection(value),
  });

  useRegisterStepSave({
    stepKey,
    registerStepSave,
    form,
  });

  useEffect(() => {
    if (product) {
      form.reset({ product: defaultValues.product });
    }
  }, [product, defaultValues, form]);

  const validateTooth = (
    tooth: number
  ): ValidationErrors<MaterialCharacteristics> | undefined => {
    const items = form.getFieldValue("product")?.items ?? [];
    const found = items.find(item => item?.tooth === tooth);
    const result = validateSchema(
      materialCharacteristicsSchema,
      found?.materialCharacteristics
    );
    return result.success ? undefined : result.errors;
  };

  if (isOrderLoading || isItemMaterialChoicesLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">
          {t("productNotFound", { keyPrefix: "common" })}
        </Alert>
      </Stack>
    );
  }

  const isSolidarized = products.some(configuredProduct =>
    SOLIDARIZED_PRODUCT_TYPE.includes(configuredProduct.type)
  );

  return (
    <Stack h="100%" w="100%" justify="space-between" p="md">
      <Stack gap="md">
        <Title order={2} c="primary">
          {t("title")}
        </Title>
        <form.Field
          name="product"
          listeners={{
            onChange: ({ fieldApi }) => {
              fieldApi.setErrorMap({
                onSubmit: undefined,
                onBlur: undefined,
              });
            },
          }}
          validators={{
            onChange: ({ value, fieldApi }) => {
              const result = validateSelection({ value });

              if (!result) {
                fieldApi.setErrorMap({
                  onSubmit: undefined,
                  onBlur: undefined,
                });
              }

              return result;
            },
            onSubmit: validateSelection,
            onBlur: validateSelection,
          }}
        >
          {field => {
            // Never display onChange errors, only onBlur and onSubmit.
            // Showing errors on onChange is too aggressive and can be confusing to the user.
            const formErrors =
              field.state.meta.errorMap.onBlur ??
              field.state.meta.errorMap.onSubmit;

            return (
              <Stack w="100%">
                <Checkbox.Card
                  p="s6"
                  radius="lg"
                  withBorder
                  bg="none"
                  checked={field.state.value.createCrown}
                  onChange={checked => {
                    const nextValue = {
                      ...field.state.value,
                      createCrown: checked,
                    };
                    field.setValue(nextValue);
                    // Unchecking makes the form valid, so it auto-submits. Checking leaves
                    // the crown incomplete: save it now so the box stays checked on return.
                    if (checked) {
                      void saveSelection({ product: nextValue });
                    }
                  }}
                >
                  <Group gap="s3" wrap="nowrap" align="flex-start">
                    <Checkbox.Indicator />
                    <Stack gap={0}>
                      <Text fw={600} size="sm">
                        {t("createCrownLabel")}
                      </Text>
                      <Text variant="caption">
                        {t("createCrownDescription")}
                      </Text>
                    </Stack>
                  </Group>
                </Checkbox.Card>
                {field.state.value.createCrown && (
                  <Group gap="s2" w="100%" wrap="nowrap" align="stretch">
                    <Divider orientation="vertical" color="brand.2" size="sm" />
                    <Box flex={1} miw={0}>
                      <ProductMaterialCharacteristicsForm
                        value={field.state.value}
                        errors={formErrors}
                        onChange={next =>
                          field.setValue({ ...field.state.value, ...next })
                        }
                        teeth={groupTeeth}
                        productType={ItemTypeEnum.crown}
                        isSolidarized={isSolidarized}
                        defaultIsPerTooth={defaultIsPerTooth}
                        itemMaterials={itemMaterials}
                        itemShadeTypes={itemShadeTypes}
                        itemShades={itemShades}
                        validateTooth={validateTooth}
                        onBlur={() => field.handleBlur()}
                      />
                    </Box>
                  </Group>
                )}
              </Stack>
            );
          }}
        </form.Field>
      </Stack>
    </Stack>
  );
};
